import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, Plus } from "lucide-react";

import { cn } from "@/lib/utils";
import TagChip from "../TagChip/TagChip";
import TagSelectPopover from "../TagSelectPopover/TagSelectPopover";

/**
 * Shared building blocks for detail-page profile headers (consultant, customer, …).
 *
 * The header is a harmonized grid of label-over-value cells. Every value area is a fixed height
 * (`h-6`) with centered content so a taller value (e.g. a 22px star `Rating`) occupies exactly the
 * same row height as a plain text value, keeping every column's rows in lockstep.
 */

interface DetailHeaderFieldProps {
	label: string;
	children: React.ReactNode;
	className?: string;
}

/** Label-over-value cell — the atom that gives a header its consistent vertical rhythm. */
export const DetailHeaderField: React.FC<DetailHeaderFieldProps> = ({
	label,
	children,
	className,
}) => (
	<div className={cn("flex flex-col gap-1", className)}>
		<span className="text-[13px] font-bold leading-tight text-muted-foreground">{label}</span>
		<div className="flex h-6 items-center text-[15px] leading-snug text-foreground">{children}</div>
	</div>
);

interface DetailHeaderTextFieldProps {
	label: string;
	value?: string | number | null;
	/** Render the value as a link (tel:/mailto:/href). */
	href?: string;
	/** Anchor target for the link (e.g. "_blank"). */
	linkTarget?: string;
	/** Show a copy-to-clipboard affordance on hover. */
	copyable?: boolean;
}

/**
 * Single-line text field — the value truncates with an ellipsis, reveals the full value in a
 * tooltip on hover when clipped, and (when `copyable`) exposes a copy-to-clipboard button on hover.
 */
export const DetailHeaderTextField: React.FC<DetailHeaderTextFieldProps> = ({
	label,
	value,
	href,
	linkTarget,
	copyable,
}) => {
	const display = value === undefined || value === null || value === "" ? "-" : String(value);
	const hasValue = display !== "-";

	const textRef = useRef<HTMLDivElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);
	const [isHovering, setIsHovering] = useState(false);

	useEffect(() => {
		const el = textRef.current;
		if (!el) return;
		const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth + 1);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, [display]);

	const showTooltip = isTruncated && isHovering;

	return (
		<div className="flex min-w-0 flex-col gap-1">
			<span className="text-[13px] font-bold leading-tight text-muted-foreground">{label}</span>
			<div
				className="group/value relative flex h-6 min-w-0 items-center gap-1"
				onMouseEnter={() => setIsHovering(true)}
				onMouseLeave={() => setIsHovering(false)}
			>
				<div
					ref={textRef}
					className={cn(
						"min-w-0 truncate text-[15px] leading-snug",
						href ? "font-bold text-action" : "text-foreground"
					)}
				>
					{href && hasValue ? (
						<a href={href} target={linkTarget} className="underline">
							{display}
						</a>
					) : (
						display
					)}
				</div>

				{copyable && hasValue && <CopyButton value={display} />}

				{showTooltip && (
					<div className="absolute left-0 top-full z-30 mt-1 max-w-xs break-words rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-normal leading-snug text-foreground shadow-md">
						{display}
					</div>
				)}
			</div>
		</div>
	);
};

/** Copy-to-clipboard icon button — hidden until its field is hovered, flips to a check on copy. */
const CopyButton: React.FC<{ value: string }> = ({ value }) => {
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(value);
			setCopied(true);
			window.setTimeout(() => setCopied(false), 1500);
		} catch {
			// Clipboard unavailable (e.g. insecure context) — silently ignore.
		}
	};

	return (
		<button
			type="button"
			onClick={copy}
			aria-label={t("COPY")}
			title={t("COPY")}
			className={cn(
				"shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition",
				"hover:text-foreground focus-visible:opacity-100 group-hover/value:opacity-100",
				copied && "opacity-100"
			)}
		>
			{copied ? <Check size={14} className="text-brand" /> : <Copy size={14} />}
		</button>
	);
};

interface ResolvedTag {
	key: string;
	name: string;
	color?: string;
	/** Hover text; defaults to the name (e.g. name + hours left for a timed tag). */
	title?: string;
}

interface DetailHeaderTagsProps {
	/** The entity's currently assigned tags, already resolved to name/color. */
	tags: ResolvedTag[];
	/** The assignable tag taxonomy (resolved). Editing is enabled when this and `onToggleTag` are set. */
	options?: ResolvedTag[];
	/** Toggle the tag with the given key on/off. When set, clicking a chip or the "+" opens a tag editor. */
	onToggleTag?: (key: string) => void;
}

/**
 * Tag row for a detail header: the assigned tags as chips plus, when editing is enabled, a condensed
 * "+" affordance. The row shrinks to its content (`inline-flex`) and is the trigger for the shared
 * {@link TagSelectPopover}, so only the chips and the "+" — not the empty row width — open the editor.
 */
export const DetailHeaderTags: React.FC<DetailHeaderTagsProps> = ({
	tags,
	options,
	onToggleTag,
}) => {
	const canEdit = !!onToggleTag && (options?.length ?? 0) > 0;

	if (tags.length === 0 && !canEdit) return null;

	const row = (
		<div
			className={cn(
				"inline-flex max-w-full flex-wrap items-center gap-1.5",
				canEdit && "cursor-pointer"
			)}
		>
			{tags.map((tag) => (
				<TagChip
					key={tag.key}
					name={tag.name}
					color={tag.color}
					title={tag.title ?? tag.name}
					showIcon
				/>
			))}
			{canEdit &&
				(tags.length === 0 ? (
					// No tags yet — spell out the affordance so it's clear what the button does.
					<span className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-gray-400 hover:bg-gray-50 hover:text-gray-900">
						<Plus size={13} />
						Lägg till tagg
					</span>
				) : (
					// With tags present the context is clear, so a condensed "+" keeps the row compact.
					<span
						aria-hidden
						className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[2px] border border-dashed border-gray-300 text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-700"
					>
						<Plus size={14} />
					</span>
				))}
		</div>
	);

	if (!canEdit) return row;

	return (
		<TagSelectPopover
			options={options!}
			selectedKeys={tags.map((tag) => tag.key)}
			onSelect={onToggleTag!}
			onDeselect={onToggleTag!}
			trigger={row}
		/>
	);
};
